import React, { useState } from 'react';
import { CodeEditorView } from '@components/CodeEditor/CodeEditorView';
import { Box } from '@mui/system';
import HorizontalTabsPage from '@components/AppNavigation/HorizontalTabsPage';
import CopyToClipboard from '@components/CopyToClipboard/CopyToClipboard';
import { grey } from '@mui/material/colors';
import { BACKEND_LANG, FRONTEND_LANG, Lang } from './types';

const BACKEND_SNIPPETS = {
	[BACKEND_LANG.go]: `// Use the code below to add the session validation to your server application.
// More info: https://docs.descope.com/build/guides/session/#Go
authorized, userToken, err :=
	descopeClient.Auth.ValidateSessionWithToken(sessionToken)`,
	[BACKEND_LANG.node]: `// Use the code below to add the session validation to your server application.
// More info: https://docs.descope.com/build/guides/session/#NodeJS
const authInfo =
	await descopeClient.validateSession(session_token);`,
	[BACKEND_LANG.python]: `# Use the code below to add the session validation to your server application.
# More info: https://docs.descope.com/build/guides/session/#Python
jwt_response =
	descope_client.validate_session(session_token=session_token)`
};

const getFrontendSnippets = (projectId: string, flowId: string) => ({
	[FRONTEND_LANG.react]: `{/* The code below includes your Project ID (${projectId}) */}
<AuthProvider projectId="${projectId}">
	<Descope
		flowId="${flowId}"
		theme="light"
		onSuccess={(e) => {
			console.log(e.detail.user.name)
			console.log(e.detail.user.email)
		}}
		onError={(err) => {
			console.log("Error!", err)
		}}
	/>
</AuthProvider>`,
	[FRONTEND_LANG.webComponents]: `<!-- The code below includes your Project ID (${projectId}) -->
<descope-wc
	project-id="${projectId}"
	flow-id="${flowId}"
	theme="light"
/>

<script>
	const wcElement = document.getElementsByTagName('descope-wc')[0];

	const onSuccess = (e) => {
			console.log(e.detail.user.name)
			console.log(e.detail.user.email)
	};
	const onError = (err) => console.log(err);

	wcElement.addEventListener('success', onSuccess);
	wcElement.addEventListener('error', onError);
</script>
`
});

const LangTypeBackend = 'backend';
const LangTypeFrontend = 'frontend';

type LangType = typeof LangTypeBackend | typeof LangTypeFrontend;

interface ICodeSnippetInnerProps {
	langType: LangType;
	lang: Lang;
	width?: string;
	value: string;
	fontSize?: string;
}

interface ICodeSnippetProps {
	langType: LangType;
	lang?: Lang;
	projectId: string;
	flowId: string;
	width?: string;
	value?: string;
	withTabs?: boolean;
	fontSize?: string;
}

const CodeSnippetInner = (props: ICodeSnippetInnerProps) => {
	const { width, langType, lang, value, fontSize } = props;
	const [copyVisible, setCopyVisible] = useState<boolean>(false);
	return (
		<Box
			fontSize={fontSize}
			sx={{ backgroundColor: grey[800] }}
			borderRadius={1}
			width="100%"
		>
			<Box
				data-testid="code-snippet"
				sx={{
					position: 'relative',
					width,
					minHeight: '50px'
				}}
				onMouseEnter={() => {
					setCopyVisible(true);
				}}
				onMouseLeave={() => {
					setCopyVisible(false);
				}}
			>
				<Box
					sx={{
						position: 'absolute',
						right: 15,
						top: 5,
						zIndex: 1
					}}
				>
					{copyVisible && (
						<CopyToClipboard
							successMessage={intl.tr(
								'code.snippet.copy.to.clipboard.message',
								{
									lang: intl.tr(
										`code.snippet.${langType}.lang.tabs.${lang}.title`
									)
								}
							)}
							tooltip={intl.tr('code.snippet.copy.to.clipboard.tooltip')}
							value={value}
							color={grey[100]}
						/>
					)}
				</Box>
				<CodeEditorView
					id="code-snippet"
					editable={false}
					onChange={() => {}}
					values={[
						{
							id: 'code-snippet',
							value
						}
					]}
					currentTheme="snippetDark"
					lang={lang}
				/>
			</Box>
		</Box>
	);
};

CodeSnippetInner.defaultProps = {
	width: undefined,
	fontSize: 'medium'
};

const CodeSnippet = (props: ICodeSnippetProps) => {
	const {
		langType,
		projectId,
		flowId,
		width,
		value,
		lang,
		withTabs,
		fontSize
	} = props;

	const [selectedLang, setSelectedLang] = React.useState(
		lang ||
			(langType === LangTypeBackend ? BACKEND_LANG.go : FRONTEND_LANG.react)
	);

	const langs = langType === LangTypeBackend ? BACKEND_LANG : FRONTEND_LANG;

	const snippets =
		langType === LangTypeBackend
			? BACKEND_SNIPPETS
			: getFrontendSnippets(projectId, flowId);

	const readyValue = value || snippets[selectedLang];

	if (!withTabs) {
		return (
			<CodeSnippetInner
				value={readyValue}
				lang={selectedLang}
				langType={langType}
				width={width}
				fontSize={fontSize}
			/>
		);
	}

	return (
		<HorizontalTabsPage
			selectedTabId={selectedLang}
			callback={(l) => {
				setSelectedLang(l);
			}}
			tabPanelSx={{ paddingTop: '8px' }}
			borderBottom={false}
			tabs={Object.keys(langs).map((l) => ({
				id: l,
				component: (
					<CodeSnippetInner
						value={readyValue}
						lang={selectedLang}
						langType={langType}
						width={width}
						fontSize={fontSize}
					/>
				)
			}))}
			routeName={`code.snippet.${langType}.lang`}
			fullRoute={`code.snippet.${langType}.lang`}
		/>
	);
};

CodeSnippet.defaultProps = {
	width: undefined,
	value: undefined,
	lang: undefined,
	withTabs: true,
	fontSize: 'medium'
};

export {
	LangTypeBackend,
	LangTypeFrontend,
	BACKEND_SNIPPETS,
	getFrontendSnippets
};
export default CodeSnippet;
